// Grocery list and pantry rules. Pure functions: take state, return new state.
import { nanoid } from 'nanoid/non-secure';
import { scaleGrocery } from './scaling';
import { addDays } from './dates';
import { toNumber, fromNumber, type Amount } from './quantity';
import { convert, resolveUnit, type CustomUnit, type UnitRef } from './units';
import type { Day, Grocery, GroceryItem, GroceryRecipe, Id, Pantry, PlanEntry, Recipe } from '../types';

/** Copies a recipe's lines into a grocery card (Swift GroceryRecipe init). */
function groceryRecipeFrom(recipe: Recipe, excludeIngredientIds: Set<Id>): GroceryRecipe {
  const items: GroceryItem[] = [
    ...recipe.ingredients
      .filter(l => !excludeIngredientIds.has(l.ingredientId))
      .map(l => ({ id: nanoid(), kind: 'ingredient' as const, refId: l.ingredientId, amount: { ...l.amount }, unit: l.unit, checked: false })),
    ...recipe.utensils.map(l => ({
      id: nanoid(), kind: 'utensil' as const, refId: l.utensilId, amount: { value: l.amount, places: 0 }, unit: null, checked: false,
    })),
  ];
  // Δ vs Swift: count is taken AFTER excluding pantry items, so a new card starts at "0 of N".
  return { id: nanoid(), recipeId: recipe.id, servings: recipe.servings, items, initialCount: items.length };
}

/**
 * Adds recipes to the grocery list. Recipes already on the list are skipped
 * (Swift addRecipes with .deduplicate). `excludeIngredientIds` drops ingredients you
 * already have (the "Deduplicate" answer to the pantry dialog).
 */
export function addRecipes(grocery: Grocery, recipes: Recipe[], excludeIngredientIds: Set<Id> = new Set()): { grocery: Grocery; added: number } {
  const present = new Set(grocery.recipes.map(r => r.recipeId));
  const additions = recipes.filter(r => !present.has(r.id) && (present.add(r.id), true)).map(r => groceryRecipeFrom(r, excludeIngredientIds));
  return { grocery: { ...grocery, recipes: [...grocery.recipes, ...additions], updatedAt: Date.now() }, added: additions.length };
}

/** Amount shown for an item: scaled by its card's servings (Swift scaledAmount), loose items as-is. */
export function displayAmount(item: GroceryItem, card: GroceryRecipe | undefined, recipeServings: number | undefined): Amount {
  if (!card || recipeServings === undefined) return item.amount;
  return scaleGrocery(item.amount, card.servings, recipeServings);
}

export function cardProgress(card: GroceryRecipe): { checked: number; total: number; done: boolean } {
  const total = Math.max(card.initialCount, card.items.length);
  const unchecked = card.items.filter(i => !i.checked).length;
  const checked = Math.max(0, total - unchecked);
  return { checked, total, done: total > 0 && checked === total };
}

export type GroceryRow = {
  key: string;
  kind: 'ingredient' | 'utensil';
  refId: Id;
  /** Summed amount in `unit` (null unit = count). */
  amount: number;
  unit: UnitRef;
  itemIds: Id[];
  recipeIds: Id[];
  checked: boolean;
};

/**
 * ➕ New vs Swift: the same ingredient from several recipes becomes ONE row when units are
 * compatible (same unit, or same dimension → converted to the first row's unit).
 * Checking a merged row checks every underlying item.
 */
export function groceryRows(
  grocery: Grocery,
  recipeServings: Record<Id, number>,
  customUnits: Record<string, CustomUnit>,
): GroceryRow[] {
  const rows: GroceryRow[] = [];
  const add = (item: GroceryItem, card?: GroceryRecipe) => {
    const value = toNumber(displayAmount(item, card, card ? recipeServings[card.recipeId] : undefined));
    const unit = resolveUnit(item.unit, customUnits);
    for (const row of rows) {
      if (row.kind !== item.kind || row.refId !== item.refId || row.checked !== item.checked) continue;
      const rowUnit = resolveUnit(row.unit, customUnits);
      if (!unit && !rowUnit) {
        merge(row, value, item, card);
        return;
      }
      if (unit && rowUnit) {
        const same = JSON.stringify(row.unit) === JSON.stringify(item.unit);
        const converted = same ? value : convert(value, unit, rowUnit);
        if (converted !== null) {
          merge(row, converted, item, card);
          return;
        }
      }
    }
    rows.push({
      key: item.id, kind: item.kind, refId: item.refId, amount: value, unit: item.unit,
      itemIds: [item.id], recipeIds: card ? [card.recipeId] : [], checked: item.checked,
    });
  };
  for (const card of grocery.recipes) for (const item of card.items) add(item, card);
  for (const item of grocery.looseItems) add(item);
  return rows;
}

function merge(row: GroceryRow, value: number, item: GroceryItem, card?: GroceryRecipe) {
  row.amount += value;
  row.itemIds.push(item.id);
  if (card && !row.recipeIds.includes(card.recipeId)) row.recipeIds.push(card.recipeId);
}

export function setChecked(grocery: Grocery, itemIds: Id[], checked: boolean): Grocery {
  const ids = new Set(itemIds);
  const flip = (i: GroceryItem) => (ids.has(i.id) ? { ...i, checked } : i);
  return {
    recipes: grocery.recipes.map(c => ({ ...c, items: c.items.map(flip) })),
    looseItems: grocery.looseItems.map(flip),
    updatedAt: Date.now(),
  };
}

/**
 * "Move to Pantry": every checked item goes into the pantry (amounts summed per ingredient
 * or utensil, Swift Pantry.store), then leaves the grocery list. Cards with nothing left
 * disappear. Expiry is today + 7 days, as in Swift.
 * Δ vs Swift: pantry keeps units and sums real values (Swift summed raw fixed-point integers
 * and dropped units, so 1.5 kg + 200 g became "215").
 */
export function moveCheckedToPantry(
  grocery: Grocery,
  pantry: Pantry,
  recipeServings: Record<Id, number>,
  customUnits: Record<string, CustomUnit>,
  today: Day,
): { grocery: Grocery; pantry: Pantry } {
  const items = [...pantry.items];
  const store = (item: GroceryItem, card?: GroceryRecipe) => {
    const value = toNumber(displayAmount(item, card, card ? recipeServings[card.recipeId] : undefined));
    if (item.kind === 'utensil') {
      const existing = items.findIndex(p => p.kind === 'utensil' && p.refId === item.refId);
      if (existing >= 0) items[existing] = { ...items[existing], amount: items[existing].amount + value, dirty: false, estimate: 'estimated' };
      else items.push({ kind: 'utensil', refId: item.refId, amount: value, unit: null, dirty: false, estimate: 'estimated' });
      return;
    }
    const unit = resolveUnit(item.unit, customUnits);
    const expiresOn = addDays(today, 7);
    for (let i = 0; i < items.length; i++) {
      const p = items[i];
      if (p.kind !== 'ingredient' || p.refId !== item.refId) continue;
      const pUnit = resolveUnit(p.unit, customUnits);
      let converted: number | null = null;
      if (!unit && !pUnit) converted = value;
      else if (unit && pUnit) converted = JSON.stringify(p.unit) === JSON.stringify(item.unit) ? value : convert(value, unit, pUnit);
      if (converted !== null) {
        items[i] = { ...p, amount: roundTo3(p.amount + converted), expiresOn, estimate: 'estimated' };
        return;
      }
    }
    items.push({ kind: 'ingredient', refId: item.refId, amount: roundTo3(value), unit: item.unit, expiresOn, estimate: 'estimated' });
  };

  // Swift order: loose ingredients, recipe ingredients, loose utensils, recipe utensils.
  // It matters because the first item stored for an ingredient decides the pantry row's unit.
  for (const kind of ['ingredient', 'utensil'] as const) {
    for (const item of grocery.looseItems) if (item.checked && item.kind === kind) store(item);
    for (const card of grocery.recipes) for (const item of card.items) if (item.checked && item.kind === kind) store(item, card);
  }

  const recipes = grocery.recipes
    .map(c => ({ ...c, items: c.items.filter(i => !i.checked) }))
    .filter(c => c.items.length > 0);
  return {
    grocery: { recipes, looseItems: grocery.looseItems.filter(i => !i.checked), updatedAt: Date.now() },
    pantry: { items, updatedAt: Date.now() },
  };
}

/** Adds ingredients straight to the pantry (a photo of the fridge, an assistant's list), merged like bought groceries. */
export function addToPantry(pantry: Pantry, items: { refId: Id; amount: Amount; unit: GroceryItem['unit'] }[], customUnits: Record<string, CustomUnit>, today: Day): Pantry {
  const looseItems: GroceryItem[] = items.map((it, i) => ({ id: `p${i}`, kind: 'ingredient', refId: it.refId, amount: it.amount, unit: it.unit, checked: true }));
  return moveCheckedToPantry({ recipes: [], looseItems, updatedAt: 0 }, pantry, {}, customUnits, today).pantry;
}

function roundTo3(n: number): number {
  return toNumber(fromNumber(n));
}

/**
 * Recipes to put on the grocery list from the planner, in date order, each once.
 * Δ vs Swift: past days are excluded (Swift included them, which re-added old meals).
 */
export function plannedRecipeIds(entries: PlanEntry[], today: Day): Id[] {
  const seen = new Set<Id>();
  return [...entries]
    .filter(e => e.date >= today)
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0))
    .map(e => e.recipeId)
    .filter(id => !seen.has(id) && (seen.add(id), true));
}

/** Ingredient ids from these recipes that are already in the pantry (drives the dedupe dialog). */
export function ingredientsAlreadyInPantry(recipes: Recipe[], pantry: Pantry): Set<Id> {
  const inPantry = new Set(pantry.items.filter(p => p.kind === 'ingredient').map(p => p.refId));
  const hits = new Set<Id>();
  for (const r of recipes) for (const l of r.ingredients) if (inPantry.has(l.ingredientId)) hits.add(l.ingredientId);
  return hits;
}

/** Every ingredient and utensil the recipe uses (recipe-level and step-level). */
export function recipeItemIds(recipe: Recipe): { ingredients: Set<Id>; utensils: Set<Id> } {
  const ingredients = new Set<Id>(recipe.ingredients.map(l => l.ingredientId));
  const utensils = new Set<Id>(recipe.utensils.map(l => l.utensilId));
  for (const s of recipe.steps) {
    s.ingredients.forEach(l => ingredients.add(l.ingredientId));
    s.utensils.forEach(l => utensils.add(l.utensilId));
  }
  return { ingredients, utensils };
}

/** "Pantry Ready" filter: every item is in the pantry; recipes with no items don't qualify. */
export function isPantryReady(recipe: Recipe, pantry: Pantry): boolean {
  const { ingredients, utensils } = recipeItemIds(recipe);
  if (ingredients.size + utensils.size === 0) return false;
  const have = new Set(pantry.items.map(p => `${p.kind}:${p.refId}`));
  return [...ingredients].every(id => have.has(`ingredient:${id}`)) && [...utensils].every(id => have.has(`utensil:${id}`));
}

